/**
 * Parses a decimal typed the Venezuelan way. The comma is the decimal separator and the dot
 * groups thousands ("1.250,50" → 1250.5, "884,75" → 884.75); a plain dot decimal ("884.75")
 * is accepted too. A dot followed by exactly three digits ("1.250") is read as a thousands
 * group, as es-VE writes it. Anything that is not a number returns NaN so the schema can
 * report it (an empty input is NaN as well).
 */
export function parseDecimal(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (typeof raw !== 'string') return Number.NaN;
  const text = raw.replace(/\s+/g, '');
  if (!/^-?[\d.,]+$/.test(text)) return Number.NaN;

  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const lastComma = body.lastIndexOf(',');
  const lastDot = body.lastIndexOf('.');

  let normalized: string;
  if (lastComma >= 0 && lastDot >= 0) {
    // Both present: the right-most one is the decimal separator, the other groups thousands.
    const decimalAt = Math.max(lastComma, lastDot);
    const thousands = lastComma > lastDot ? '.' : ',';
    normalized =
      body.slice(0, decimalAt).split(thousands).join('') + '.' + body.slice(decimalAt + 1);
  } else if (lastComma >= 0) {
    if (body.indexOf(',') !== lastComma) return Number.NaN;
    normalized = body.replace(',', '.');
  } else if (/^[1-9]\d{0,2}(\.\d{3})+$/.test(body)) {
    normalized = body.split('.').join('');
  } else {
    if (body.indexOf('.') !== lastDot) return Number.NaN;
    normalized = body;
  }

  if (!/^\d+(\.\d+)?$|^\.\d+$|^\d+\.$/.test(normalized)) return Number.NaN;
  const value = Number(normalized);
  return negative ? -value : value;
}

/** `register(name, { setValueAs: decimalOrUndefined })` for optional decimal fields. */
export function decimalOrUndefined(raw: unknown): number | undefined {
  if (typeof raw === 'string' && raw.trim() === '') return undefined;
  return parseDecimal(raw);
}

/** Formats a number for an input the user will edit (comma decimal, no thousands dots). */
export function formatDecimalInput(value: number): string {
  return String(value).replace('.', ',');
}
